"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { FiMinus, FiPlus } from "react-icons/fi";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import { ChevronDownIcon } from "@/app/components/icons/ChevronDownIcon";
import { BookOpenTextIcon } from "@/app/components/icons/BookOpenTextIcon";
import { useMarketPriceOptional } from "../context/MarketPriceContext";
import { useMarketRewardsParams } from "@/hooks/useMarketRewardsParams";
import { useT } from "@/hooks/useT";

interface OrderLevel {
  price: string;
  size: string;
}

interface OrderBookData {
  bids: OrderLevel[];
  asks: OrderLevel[];
  timestamp: number;
}

function normalizeOrderBook(data: any): OrderBookData | null {
  if (!data || Array.isArray(data)) return null;
  const book = data.data || data;
  if (!book || (!book.bids && !book.asks)) return null;
  return {
    bids: Array.isArray(book.bids) ? book.bids : [],
    asks: Array.isArray(book.asks) ? book.asks : [],
    timestamp: Number(book.timestamp) || Date.now(),
  };
}

interface OrderBookProps {
  tokenIds: string[];
  outcomes: string[];
  alwaysExpanded?: boolean;
  /** Hide the title/toggle header when rendered inside a tab that already labels it. */
  hideHeader?: boolean;
  acceptingOrders?: boolean;
  initialOutcomeIndex?: number;
  source?: "polymarket" | "kalshi" | "predict";
  predictMarketId?: string;
  orderbookApi?: "default" | "sports-live";
  /** Pre-fetched data from parent — skips internal fetching when provided */
  externalData?: { yes: { bids: any[]; asks: any[]; timestamp: number } | null; no: { bids: any[]; asks: any[]; timestamp: number } | null } | null;
  /** Polymarket condition ID — when provided, the book shades price levels
   * inside the liquidity-rewards band (midpoint ± maxSpread) so makers can
   * eyeball which rungs qualify for rebates. Fetched via the shared
   * `useMarketRewardsParams` cache so there's no extra network cost. */
  conditionId?: string | null;
}

export default function OrderBook({
  tokenIds,
  outcomes,
  alwaysExpanded,
  hideHeader,
  acceptingOrders,
  initialOutcomeIndex = 0,
  source = "polymarket",
  predictMarketId,
  orderbookApi = "default",
  externalData,
  conditionId,
}: OrderBookProps) {
  const t = useT();
  const [yesBook, setYesBook] = useState<OrderBookData | null>(null);
  const [noBook, setNoBook] = useState<OrderBookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeOutcome, setActiveOutcome] = useState<"YES" | "NO">("YES");
  // Tick size in cents. PM defaults to 0.1¢ granularity; prior default of 1
  // rounded 16.1/16.3/16.4 into a single "16" bucket and hid the real book.
  const [precision, setPrecision] = useState(0.1);
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded || false);
  const [headerHovered, setHeaderHovered] = useState(false);
  const visRef = useVisibilityRef();
  const prevBidsRef = useRef<Map<number, number>>(new Map());
  const prevAsksRef = useRef<Map<number, number>>(new Map());
  // Scroll refs — asks column auto-scrolls to the bottom on mount so the best
  // ask (closest to mid) sits right above the spread row, matching PM.
  const asksScrollRef = useRef<HTMLDivElement>(null);
  const [flashedRows, setFlashedRows] = useState<Set<string>>(new Set());

  // Use shared price context for consistent price display
  const priceContext = useMarketPriceOptional();

  // When external data is provided, sync it to local state (no internal fetching)
  useEffect(() => {
    if (!externalData) return;
    if (externalData.yes) setYesBook(externalData.yes);
    if (externalData.no) setNoBook(externalData.no);
    setLoading(false);
  }, [externalData]);

  useEffect(() => {
    setActiveOutcome(initialOutcomeIndex === 1 ? "NO" : "YES");
  }, [initialOutcomeIndex]);

  useEffect(() => {
    if (externalData) return;
    setYesBook(null);
    setNoBook(null);
    setLoading(true);
  }, [externalData, orderbookApi, predictMarketId, source, tokenIds]);

  useEffect(() => {
    // Skip internal fetching when parent provides data
    if (externalData) return;
    if (source === "predict" && !predictMarketId) return;
    if (source !== "predict" && (!tokenIds || tokenIds.length < 2)) return;
    // Only poll when expanded — saves 40 requests/min when collapsed
    if (!isExpanded && !alwaysExpanded) return;

    async function fetchOrderBooks() {
      try {
        if (!yesBook && !noBook) setLoading(true);

        if (source === "predict" && predictMarketId) {
          // Predict.fun: single orderbook endpoint with YES-side asks/bids
          const res = await fetch(`/api/markets/predict/${predictMarketId}/orderbook`);
          if (res.ok) {
            const data = await res.json();
            const ob = data.orderbook;
            if (ob) {
              // asks = sell side (YES), bids = buy side (YES)
              setYesBook({ asks: ob.asks || [], bids: ob.bids || [], timestamp: ob.timestamp || Date.now() });
              // Derive NO book by inverting: NO bids = 1-ask price, NO asks = 1-bid price
              setNoBook({
                asks: (ob.bids || []).map((b: OrderLevel) => ({ price: (1 - parseFloat(b.price)).toFixed(4), size: b.size })),
                bids: (ob.asks || []).map((a: OrderLevel) => ({ price: (1 - parseFloat(a.price)).toFixed(4), size: a.size })),
                timestamp: ob.timestamp || Date.now(),
              });
            }
          }
        } else {
          const buildPolymarketOrderbookUrl = (tokenId: string) =>
            orderbookApi === "sports-live"
              ? `/api/sports-live/clob/book?token_id=${tokenId}`
              : `/api/polymarket/orderbook?tokenId=${tokenId}`;

          // Polymarket: two separate orderbooks per token
          const [yesResponse, noResponse] = await Promise.all([
            fetch(buildPolymarketOrderbookUrl(tokenIds[0])),
            fetch(buildPolymarketOrderbookUrl(tokenIds[1]))
          ]);

          if (yesResponse.ok) {
            const yesData = await yesResponse.json();
            const normalized = normalizeOrderBook(yesData);
            if (normalized) setYesBook(normalized);
          }

          if (noResponse.ok) {
            const noData = await noResponse.json();
            const normalized = normalizeOrderBook(noData);
            if (normalized) setNoBook(normalized);
          }
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    }

    fetchOrderBooks();
    // Don't poll closed/settled markets — only show last known data
    if (acceptingOrders === false) return;
    const interval = setInterval(() => { if (visRef.current) fetchOrderBooks(); }, 10000);
    return () => clearInterval(interval);
  }, [tokenIds, isExpanded, acceptingOrders, source, predictMarketId, orderbookApi, externalData, alwaysExpanded]);

  const currentBook = activeOutcome === "YES" ? yesBook : noBook;
  const outcome1Label = outcomes[0]?.toUpperCase() || "YES";
  const outcome2Label = outcomes[1]?.toUpperCase() || "NO";

  const { aggregatedAsks, aggregatedBids, maxCumulativeSize, spreadPrice, midPrice, bestAskCents, bestBidCents } = useMemo(() => {
    if (!currentBook) return { aggregatedAsks: [], aggregatedBids: [], maxCumulativeSize: 1, spreadPrice: 0, midPrice: 0, bestAskCents: 0, bestBidCents: 0 };

    const aggregateOrders = (orders: OrderLevel[], isAsk: boolean) => {
      const grouped = new Map<number, number>();

      orders.forEach(order => {
        const price = parseFloat(order.price) * 100;
        const size = parseFloat(order.size);
        const roundedPrice = Math.round(price / precision) * precision;
        grouped.set(roundedPrice, (grouped.get(roundedPrice) || 0) + size);
      });

      const sorted = Array.from(grouped.entries())
        .sort((a, b) => isAsk ? a[0] - b[0] : b[0] - a[0]);

      // Track both cumulative shares (for the depth bar scale) and cumulative
      // USD (shares × price per level) — PM's "Total" column is USD, not
      // shares. Accumulation runs from best price outward, matching PM.
      let cumulative = 0;
      let cumulativeUsd = 0;
      return sorted.map(([price, size]) => {
        cumulative += size;
        // price is in cents (0-100); convert to dollars for USD total.
        cumulativeUsd += size * (price / 100);
        return { price, size, cumulative, cumulativeUsd };
      });
    };

    const asks = aggregateOrders(currentBook.asks || [], true);
    const bids = aggregateOrders(currentBook.bids || [], false);

    const maxCum = Math.max(
      ...asks.map(a => a.cumulative),
      ...bids.map(b => b.cumulative),
      1
    );

    const bestAsk = asks[0]?.price || 0;
    const bestBid = bids[0]?.price || 0;
    const spread = bestAsk - bestBid;
    const mid = bestBid > 0 ? bestBid : bestAsk;

    return {
      aggregatedAsks: asks.reverse(),
      aggregatedBids: bids,
      maxCumulativeSize: maxCum,
      spreadPrice: spread,
      midPrice: mid,
      bestAskCents: bestAsk,
      bestBidCents: bestBid,
    };
  }, [currentBook, precision]);

  // Rewards band (Polymarket only) — fetched via the shared cache so the
  // side-effect is free when another widget (TradingPanel, EventRewardsBadge)
  // already hydrated it. Shading is scoped to the current outcome: each
  // side has its own midpoint derived from its own best-bid/ask, which is
  // the same calculation the CLOB uses to score makers.
  const rewardsParamsQ = useMarketRewardsParams(conditionId || undefined, source === "polymarket" && !!conditionId);
  const rewardsBand = useMemo(() => {
    const p = rewardsParamsQ.data;
    if (!p || p.ratePerDay <= 0 || p.maxSpread <= 0) return null;
    if (bestAskCents <= 0 || bestBidCents <= 0) return null;
    const mid = (bestAskCents + bestBidCents) / 2;
    return {
      lowCents: Math.max(0.1, mid - p.maxSpread),
      highCents: Math.min(99.9, mid + p.maxSpread),
      ratePerDay: p.ratePerDay,
    };
  }, [rewardsParamsQ.data, bestAskCents, bestBidCents]);
  const isInRewardsBand = (priceCents: number): boolean => {
    if (!rewardsBand) return false;
    return priceCents >= rewardsBand.lowCents && priceCents <= rewardsBand.highCents;
  };

  // Flash animation when prices change
  useEffect(() => {
    const newFlashed = new Set<string>();
    aggregatedBids.forEach(order => {
      const prev = prevBidsRef.current.get(order.price);
      if (prev !== undefined && prev !== order.size) {
        newFlashed.add(`bid-${order.price}`);
      }
    });
    aggregatedAsks.forEach(order => {
      const prev = prevAsksRef.current.get(order.price);
      if (prev !== undefined && prev !== order.size) {
        newFlashed.add(`ask-${order.price}`);
      }
    });
    if (newFlashed.size > 0) {
      setFlashedRows(newFlashed);
      const t = setTimeout(() => setFlashedRows(new Set()), 600);
      // Update refs
      prevBidsRef.current = new Map(aggregatedBids.map(o => [o.price, o.size]));
      prevAsksRef.current = new Map(aggregatedAsks.map(o => [o.price, o.size]));
      return () => clearTimeout(t);
    }
    prevBidsRef.current = new Map(aggregatedBids.map(o => [o.price, o.size]));
    prevAsksRef.current = new Map(aggregatedAsks.map(o => [o.price, o.size]));
  }, [aggregatedBids, aggregatedAsks]);

  // Pin the asks column to the bottom so the best ask sits right above the
  // spread row (PM layout). Double rAF + ResizeObserver covers the cases
  // where the DOM hasn't reflowed yet on the first effect pass, and where
  // the row count grows after initial paint (live book updates).
  useEffect(() => {
    const el = asksScrollRef.current;
    if (!el) return;
    const pinBottom = () => { el.scrollTop = el.scrollHeight; };
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(pinBottom);
      (el as any).__rafInner = raf2;
    });
    const ro = new ResizeObserver(pinBottom);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf1);
      const inner = (el as any).__rafInner;
      if (inner) cancelAnimationFrame(inner);
      ro.disconnect();
    };
  }, [aggregatedAsks]);

  return (
    <div className={hideHeader ? "" : "bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors overflow-hidden"}>
      {/* Header */}
      {!hideHeader && (
        <button
          onClick={() => !alwaysExpanded && setIsExpanded(!isExpanded)}
          onMouseEnter={() => setHeaderHovered(true)}
          onMouseLeave={() => setHeaderHovered(false)}
          className={`w-full flex items-center justify-between px-4 py-3 transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400/50 focus-visible:outline-none ${
            alwaysExpanded ? 'cursor-default' : 'hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50'
          }`}
        >
          <div className="flex items-center gap-2">
            <BookOpenTextIcon size={18} className="text-gray-400 dark:text-gray-500" isHovered={headerHovered} />
            <h3 className="font-monument text-lg text-gray-900 dark:text-white">{t("orderBook.title")}</h3>
          </div>
          {!alwaysExpanded && (
            <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
              <ChevronDownIcon size={20} className="text-gray-400" isHovered={headerHovered} />
            </div>
          )}
        </button>
      )}

      {/* Collapsible Content */}
      <div className={`transition-[max-height,opacity] duration-200 ease-in-out ${(isExpanded || alwaysExpanded) ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        {/* Loading State */}
        {loading && !yesBook && !noBook && (
          <div className="px-4 pb-4">
            <div className="flex items-center gap-2 px-1 mb-3">
              <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin" />
              <span className="text-xs text-gray-500 dark:text-gray-500">{t("orderBook.loading")}</span>
            </div>
            <div className="space-y-1">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="h-5 skeleton-shimmer rounded" style={{ animationDelay: `${i * 50}ms` }} />
              ))}
            </div>
          </div>
        )}

        {/* Content when loaded */}
        {(!loading || yesBook || noBook) && (
          <>
            {/* Polymarket-style header: "TRADE YES / NO" toggle on the left,
                precision control on the right. Replaces the full-width tab
                bar and the ornate precision stepper — tighter and quieter. */}
            <div className="flex items-center justify-between px-3 pb-2 border-b border-gray-200 dark:border-[#1A1A1A]/50">
              <div className="flex items-center gap-1 text-[11px] font-inter-tight font-semibold uppercase tracking-wide">
                <button
                  onClick={() => setActiveOutcome("YES")}
                  className={`px-2 py-1 rounded transition-colors ${
                    activeOutcome === "YES"
                      ? "text-[#00FF9D] bg-[#00FF9D]/10"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  {t("orderBook.trade", { label: outcome1Label })}
                </button>
                <button
                  onClick={() => setActiveOutcome("NO")}
                  className={`px-2 py-1 rounded transition-colors ${
                    activeOutcome === "NO"
                      ? "text-[#FF1744] bg-[#FF1744]/10"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  {t("orderBook.trade", { label: outcome2Label })}
                </button>
              </div>
              {/* Tick size stepper — cycles through 0.1¢ / 1¢ / 10¢ buckets.
                  PM uses 0.1¢ as the default; coarser ticks are useful on
                  sparse books. */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#0f0f12] rounded p-0.5">
                {(() => {
                  const PRESETS = [0.1, 1, 10];
                  const idx = PRESETS.indexOf(precision);
                  const safeIdx = idx < 0 ? 0 : idx;
                  const prev = () => setPrecision(PRESETS[Math.max(0, safeIdx - 1)]);
                  const next = () => setPrecision(PRESETS[Math.min(PRESETS.length - 1, safeIdx + 1)]);
                  return (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        className="p-0.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors focus-visible:outline-none rounded"
                      >
                        <FiMinus className="w-3 h-3" />
                      </button>
                      <span className="font-inter-tight text-[10px] text-gray-600 dark:text-gray-300 min-w-[24px] text-center">
                        {precision < 1 ? precision.toFixed(1) : precision.toFixed(0)}¢
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        className="p-0.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors focus-visible:outline-none rounded"
                      >
                        <FiPlus className="w-3 h-3" />
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Column Headers — Price | Shares | Total (Polymarket layout).
                pl-12 reserves a left gutter for the absolute-positioned
                Asks/Bids pills so they never overlap the price column. */}
            <div className="grid grid-cols-3 pl-12 pr-3 py-1.5 text-[10px] font-inter-tight font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide border-b border-gray-200 dark:border-[#1A1A1A]/30">
              <div>{t("orderBook.colPrice")}</div>
              <div className="text-right">{t("orderBook.colShares")}</div>
              <div className="text-right">{t("orderBook.colTotal")}</div>
            </div>

            {/* Order Book Content — PM layout: Asks pill + left-filling depth
                bars, Bids pill + same left-filling bars, mid-row with last
                price + spread. */}
            <div className="relative">
              {/* Asks side — scrollable box sized to ~4 rows. "Asks" pill
                  sits against the bottom-left edge (closest to the spread
                  row) so it never collides with the "Bids" pill. */}
              <div className="relative">
                <span className="absolute bottom-1 left-2 z-10 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-[#FF1744]/15 text-[#FF1744] pointer-events-none">
                  {t("orderBook.asks")}
                </span>
                <div
                  ref={asksScrollRef}
                  className="space-y-px overflow-y-auto orderbook-scroll"
                  style={{ maxHeight: '400px' }}
                >
                  {aggregatedAsks.map((order, i) => {
                    const barWidth = (order.cumulative / maxCumulativeSize) * 100;
                    const isFlashed = flashedRows.has(`ask-${order.price}`);
                    const inBand = isInRewardsBand(order.price);
                    return (
                      <div key={`ask-${i}`} className={`relative h-6 flex items-center ${isFlashed ? 'animate-flash-row' : ''} ${inBand ? 'bg-yellow-400/15 dark:bg-yellow-300/[0.12]' : ''}`}>
                        <div
                          className="absolute left-0 top-0 bottom-0 bg-[#FF1744]/10"
                          style={{ width: `${barWidth}%` }}
                        />
                        <div className="relative grid grid-cols-3 w-full pl-12 pr-3 text-[11px] tabular-nums">
                          <div className="font-inter-tight font-semibold text-[#FF1744]">
                            {(order.price ?? 0).toFixed(precision < 1 ? 1 : 0)}¢
                          </div>
                          <div className="font-satoshi text-gray-900 dark:text-white text-right">
                            {order.size.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </div>
                          <div className="font-satoshi text-gray-500 dark:text-gray-400 text-right">
                            ${order.cumulativeUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Mid-row — Last + Spread, aligned left/center like PM. */}
              <div className="py-1.5 px-3 border-y border-gray-200 dark:border-[#1A1A1A]/30 flex items-center justify-between text-[11px] font-satoshi">
                <span className="text-gray-500 dark:text-gray-400">
                  {t("orderBook.last")}{' '}
                  <span className={`font-inter-tight font-semibold ${activeOutcome === 'YES' ? 'text-[#00FF9D]' : 'text-[#FF1744]'}`}>
                    {priceContext?.buyPrices
                      ? ((activeOutcome === 'YES' ? priceContext.buyPrices.buyYes : priceContext.buyPrices.buyNo) * 100).toFixed(1)
                      : (midPrice ?? 0).toFixed(1)}¢
                  </span>
                </span>
                {spreadPrice > 0 && (
                  <span className="text-gray-500 dark:text-gray-400">
                    {t("orderBook.spread")} <span className="text-gray-700 dark:text-gray-300 font-medium">{(spreadPrice ?? 0).toFixed(1)}¢</span>
                  </span>
                )}
              </div>

              {/* Bids side — pill at top-left, right below the spread row,
                  mirroring asks. Same scrollable ~4-row box. */}
              <div className="relative">
                <span className="absolute top-1 left-2 z-10 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-[#00FF9D]/15 text-[#00FF9D] pointer-events-none">
                  {t("orderBook.bids")}
                </span>
                <div
                  className="space-y-px overflow-y-auto orderbook-scroll"
                  style={{ maxHeight: '400px' }}
                >
                  {aggregatedBids.map((order, i) => {
                    const barWidth = (order.cumulative / maxCumulativeSize) * 100;
                    const isFlashed = flashedRows.has(`bid-${order.price}`);
                    const inBand = isInRewardsBand(order.price);
                    return (
                      <div key={`bid-${i}`} className={`relative h-6 flex items-center ${isFlashed ? 'animate-flash-row' : ''} ${inBand ? 'bg-yellow-400/15 dark:bg-yellow-300/[0.12]' : ''}`}>
                        <div
                          className="absolute left-0 top-0 bottom-0 bg-[#00FF9D]/10"
                          style={{ width: `${barWidth}%` }}
                        />
                        <div className="relative grid grid-cols-3 w-full pl-12 pr-3 text-[11px] tabular-nums">
                          <div className="font-inter-tight font-semibold text-[#00FF9D]">
                            {(order.price ?? 0).toFixed(precision < 1 ? 1 : 0)}¢
                          </div>
                          <div className="font-satoshi text-gray-900 dark:text-white text-right">
                            {order.size.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </div>
                          <div className="font-satoshi text-gray-500 dark:text-gray-400 text-right">
                            ${order.cumulativeUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-3 py-2 border-t border-gray-200 dark:border-[#1A1A1A]/30 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
              <span className="font-satoshi flex items-center gap-2">
                {t("orderBook.levels", { n: aggregatedAsks.length + aggregatedBids.length })}
                {rewardsBand && (
                  <span
                    className="inline-flex items-center gap-1 text-yellow-700 dark:text-yellow-500"
                    title={t("orderBook.rewardsBandTooltip", { low: rewardsBand.lowCents.toFixed(1), high: rewardsBand.highCents.toFixed(1), rate: rewardsBand.ratePerDay.toFixed(2) })}
                  >
                    <span className="inline-block w-2 h-2 rounded-sm bg-yellow-400/40 dark:bg-yellow-300/30 ring-1 ring-yellow-400/50" />
                    {t("orderBook.rewardsBand")}
                  </span>
                )}
              </span>
              <span className="font-satoshi">
                {t("orderBook.liveUpdates")}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
